import type { BaseResponse, Event, EventGuest } from "@anggunpraya-templates/types";
import { useEventGuestStore } from "../stores/event-guest";

/**
 * Opsi untuk `useEventGuestLoader()`.
 *
 * `previewGuest` tetap datang dari wrapper template karena setiap template
 * masih memiliki mock guest preview yang berbeda. Dengan pola ini, policy
 * load guest bisa dishared di `base` tanpa memaksa data preview ikut dipusatkan.
 */
interface UseEventGuestLoaderOptions {
  previewGuest: EventGuest | null;
}

/**
 * Composable shared untuk memuat guest undangan aktif.
 *
 * Boundary yang dijaga oleh composable ini:
 * - store `event-guest` hanya menyimpan state
 * - composable ini memegang policy `preview` / `portfolio` / `event`
 * - wrapper template dipakai untuk memasok data preview lokal
 *
 * @param options Opsi loader, termasuk guest preview bawaan template
 * @returns Objek utilitas loader yang saat ini mengekspos `loadEventGuest()`
 *
 * @example
 * ```typescript
 * const { loadEventGuest } = useEventGuestLoader({
 *   previewGuest: eventGuestPreviewData,
 * });
 *
 * await loadEventGuest(route.params.code as string);
 * ```
 */
export function useEventGuestLoader(options?: UseEventGuestLoaderOptions) {
  const { $services } = useNuxtApp();
  const { flavor } = useRuntimeConfig().public;

  const { data: eventDetail } = useNuxtData<BaseResponse<Event>>("event-detail");
  const eventGuestStore = useEventGuestStore();
  const { setEventGuest, setLoading } = eventGuestStore;

  /**
   * Memuat guest berdasarkan `code` route aktif.
   *
   * Untuk mode `preview`, `portfolio`, atau sentinel `EVENT_PREVIEW`,
   * data diambil dari mock template. Untuk flavor `event`, data diambil
   * dari API menggunakan `event_id` hasil fetch detail event.
   *
   * @param code Kode guest dari route undangan
   * @returns Promise yang selesai setelah store guest selesai diperbarui
   */
  async function loadEventGuest(code: string) {
    try {
      setLoading(true);

      if (["portfolio", "preview"].includes(flavor) || code === "EVENT_PREVIEW") {
        setEventGuest(options?.previewGuest || null);

        return;
      }

      const res = await $services.event.guest.getDetail(
        eventDetail.value?.data?.event_id || "",
        {
          code,
        },
      );

      setEventGuest(res.data ?? null);
    }
    catch (error) {
      if (error instanceof Error) {
        const message = error.message || "Terjadi kesalahan saat memuat data.";

        console.error(message);
        showError({
          fatal: true,
          statusCode: 500,
          message,
        });
      }
    }
    finally {
      setLoading(false);
    }
  }

  return {
    loadEventGuest,
  };
}
